import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllEvents,
  getRegistrations,
  getAllSponsors,
  getAllAnnouncements,
} from '../../services/apiClient';
import { useAuth } from '../../modules/auth/AuthContext';
import { Card } from '../../components/ui/Card';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { formatShortDate } from '../../lib/formatting';
import styles from './AdminDashboard.module.css';

interface Stats {
  totalEvents: number;
  publishedEvents: number;
  totalRegistrations: number;
  pendingRegistrations: number;
  activeSponsors: number;
  publishedAnnouncements: number;
}

export function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats>({
    totalEvents: 0,
    publishedEvents: 0,
    totalRegistrations: 0,
    pendingRegistrations: 0,
    activeSponsors: 0,
    publishedAnnouncements: 0,
  });
  const [recentRegs, setRecentRegs] = useState<Awaited<ReturnType<typeof getRegistrations>>['data']>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Awaited<ReturnType<typeof getAllEvents>>['data']>([]);

  useEffect(() => {
    Promise.all([
      getAllEvents(),
      getRegistrations(),
      getAllSponsors(),
      getAllAnnouncements(),
    ]).then(([events, regs, sponsors, announcements]) => {
      const evts = events.data ?? [];
      const rs = regs.data ?? [];
      const spons = sponsors.data ?? [];
      const anns = announcements.data ?? [];

      setStats({
        totalEvents: evts.length,
        publishedEvents: evts.filter((e) => e.status === 'published').length,
        totalRegistrations: rs.length,
        pendingRegistrations: rs.filter((r) => r.registration_status === 'pending').length,
        activeSponsors: spons.filter((s) => s.active).length,
        publishedAnnouncements: anns.filter((a) => a.published).length,
      });

      setRecentRegs(rs.slice(-5).reverse());
      setUpcomingEvents(
        evts
          .filter((e) => e.status === 'published' && new Date(e.start_date) > new Date())
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
          .slice(0, 4)
      );
    });
  }, []);

  return (
    <div className={styles.page}>
      <p className={styles.welcome}>
        Welcome back, <strong>{user?.name}</strong>.
      </p>

      {/* Summary Cards */}
      <div className={styles.statsGrid}>
        <StatCard label="Total Events" value={stats.totalEvents} sub={`${stats.publishedEvents} published`} href="/admin/events" />
        <StatCard label="Registrations" value={stats.totalRegistrations} sub={`${stats.pendingRegistrations} pending`} href="/admin/registrations" />
        <StatCard label="Active Sponsors" value={stats.activeSponsors} href="/admin/sponsors" />
        <StatCard label="Announcements" value={stats.publishedAnnouncements} sub="published" href="/admin/news" />
      </div>

      <div className={styles.panels}>
        {/* Upcoming Events */}
        <Card padding="none">
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Upcoming Events</h2>
            <Link to="/admin/events" className={styles.panelLink}>Manage →</Link>
          </div>
          {upcomingEvents && upcomingEvents.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {upcomingEvents.map((evt) => (
                  <tr key={evt.id}>
                    <td>{evt.title}</td>
                    <td>{formatShortDate(evt.start_date)}</td>
                    <td><StatusBadge label={evt.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.empty}>No upcoming events</p>
          )}
        </Card>

        {/* Recent Registrations */}
        <Card padding="none">
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Recent Registrations</h2>
            <Link to="/admin/registrations" className={styles.panelLink}>View all →</Link>
          </div>
          {recentRegs && recentRegs.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentRegs.map((reg) => (
                  <tr key={reg.id}>
                    <td>{reg.name}</td>
                    <td><StatusBadge label={reg.registration_status} /></td>
                    <td>{formatShortDate(reg.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.empty}>No registrations yet</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  href,
}: {
  label: string;
  value: number;
  sub?: string;
  href: string;
}) {
  return (
    <Link to={href} className={styles.statCard}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
      {sub && <span className={styles.statSub}>{sub}</span>}
    </Link>
  );
}
